// Sticker outline and drawing area, in the sticker's own SVG units.
//
// Stickers configured in the admin with "SVG beállítások" (view_box + d) use
// that outline. Otherwise the outline is derived from the "height" ratio
// field (e.g. "3 / 1"), so every sticker can be previewed and printed.

const DEFAULT_HEIGHT = 100;

export function parseRatio(value) {
    if (typeof value !== "string" && typeof value !== "number") {
        return 3;
    }
    const parts = String(value).split("/");
    const a = parseFloat(parts[0]);
    const b = parts.length > 1 ? parseFloat(parts[1]) : 1;
    if (!isFinite(a) || !isFinite(b) || a <= 0 || b <= 0) {
        return 3;
    }
    return a / b;
}

function parseViewBox(value) {
    if (!value) {
        return null;
    }
    const nums = String(value)
        .trim()
        .split(/[\s,]+/)
        .map(parseFloat);
    if (nums.length !== 4 || nums.some(n => !isFinite(n)) || nums[2] <= 0) {
        return null;
    }
    if (nums[3] <= 0) {
        return null;
    }
    return { x: nums[0], y: nums[1], width: nums[2], height: nums[3] };
}

function round(n) {
    return Math.round(n * 100) / 100;
}

function roundedRectPath(x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    return [
        `M${round(x + r)} ${round(y)}`,
        `H${round(x + w - r)}`,
        `A${round(r)} ${round(r)} 0 0 1 ${round(x + w)} ${round(y + r)}`,
        `V${round(y + h - r)}`,
        `A${round(r)} ${round(r)} 0 0 1 ${round(x + w - r)} ${round(y + h)}`,
        `H${round(x + r)}`,
        `A${round(r)} ${round(r)} 0 0 1 ${round(x)} ${round(y + h - r)}`,
        `V${round(y + r)}`,
        `A${round(r)} ${round(r)} 0 0 1 ${round(x + r)} ${round(y)}`,
        "Z"
    ].join(" ");
}

function ellipsePath(x, y, w, h) {
    const rx = w / 2;
    const ry = h / 2;
    const cx = x + rx;
    return [
        `M${round(cx)} ${round(y)}`,
        `A${round(rx)} ${round(ry)} 0 1 1 ${round(cx)} ${round(y + h)}`,
        `A${round(rx)} ${round(ry)} 0 1 1 ${round(cx)} ${round(y)}`,
        "Z"
    ].join(" ");
}

export function isRoundSticker(sticker) {
    return String((sticker && sticker.sku) || "")
        .toLowerCase()
        .includes("round");
}

export function getGeometry(sticker) {
    const round = isRoundSticker(sticker);
    const viewBox = parseViewBox(sticker && sticker.view_box);
    const customPath = sticker && sticker.d ? String(sticker.d).trim() : "";

    let box;
    if (viewBox) {
        box = viewBox;
    } else {
        const ratio = round ? 1 : parseRatio(sticker && sticker.height);
        box = {
            x: 0,
            y: 0,
            width: DEFAULT_HEIGHT * ratio,
            height: DEFAULT_HEIGHT
        };
    }

    let shape = customPath;
    if (!shape) {
        shape = round
            ? ellipsePath(box.x, box.y, box.width, box.height)
            : roundedRectPath(
                  box.x,
                  box.y,
                  box.width,
                  box.height,
                  Math.min(box.width, box.height) * 0.18
              );
    }

    // Area that is safe to draw text and figures in.
    let content;
    if (round) {
        // Rectangle inscribed in the ellipse, slightly inset.
        const insetX = (box.width * (1 - Math.SQRT1_2)) / 2;
        const insetY = (box.height * (1 - Math.SQRT1_2)) / 2;
        content = {
            x: box.x + insetX,
            y: box.y + insetY,
            width: box.width - insetX * 2,
            height: box.height - insetY * 2
        };
    } else {
        const pad = Math.min(box.width, box.height) * 0.08;
        content = {
            x: box.x + pad,
            y: box.y + pad,
            width: box.width - pad * 2,
            height: box.height - pad * 2
        };
    }

    return {
        viewBox: box,
        shape,
        content,
        isRound: round,
        aspectRatio: box.width / box.height
    };
}
